import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { cartApi } from "@/lib/commerce";
import { formatMoney } from "@/lib/format";
import { orderStatusLabel } from "@/lib/orderStatus";

export default function OrderConfirmation() {
  const { orderNumber } = useParams();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    cartApi.order(orderNumber).then(setOrder).catch((e) => {
      setError(e.response?.status === 404 ? "Order not found." : "Failed to load order.");
    });
  }, [orderNumber]);

  if (error) return <div className="p-10 text-sm text-destructive">{error}</div>;
  if (!order) return <div className="p-10 text-sm text-muted-foreground">Loading…</div>;

  const t = order.totals;
  return (
    <div className="min-h-screen bg-background" data-testid="order-root">
      <header className="border-b bg-white">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="font-semibold">FAST FORCE</Link>
          <Link to="/catalog" className="text-sm text-muted-foreground hover:text-foreground">Continue shopping</Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-10 space-y-6">
        <Card>
          <CardHeader>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Order placed</div>
            <CardTitle className="text-2xl">
              Thank you, {order.customer.name.split(" ")[0]}!
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            <div>Order number: <span className="font-mono" data-testid="order-number">{order.order_number}</span></div>
            <div>Status: <Badge variant="secondary" data-testid="order-status">{orderStatusLabel(order.status)}</Badge></div>
            <div className="text-muted-foreground">
              A member of our team will contact you on {order.customer.phone} to confirm your delivery. You&rsquo;ll see the status change here as we move the order along.
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base">Items</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            {order.items.map((it) => (
              <div key={it.sku} className="flex justify-between gap-2">
                <span className="truncate">{it.name} × {it.qty}</span>
                <span className="shrink-0">{formatMoney({ amount_minor: it.line_total_minor, currency: it.currency })}</span>
              </div>
            ))}
            <Separator />
            <div className="flex justify-between"><span>Subtotal</span><span>{formatMoney({ amount_minor: t.subtotal_minor, currency: t.currency })}</span></div>
            <div className="flex justify-between"><span>VAT</span><span>{formatMoney({ amount_minor: t.tax_minor, currency: t.currency })}</span></div>
            <div className="flex justify-between" data-testid="order-delivery-line">
              <span>Delivery{order.delivery_address?.emirate ? ` (${order.delivery_address.emirate})` : ""}</span>
              <span>
                {Number(t.delivery_fee_minor || 0) > 0
                  ? formatMoney({ amount_minor: t.delivery_fee_minor, currency: t.currency })
                  : "Free"}
              </span>
            </div>
            <Separator />
            <div className="flex justify-between font-semibold text-base">
              <span>Total</span>
              <span>{formatMoney({ amount_minor: t.grand_total_minor, currency: t.currency })}</span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base">Delivery</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-1">
            <div>{order.delivery_address.building}, {order.delivery_address.area}</div>
            <div>{order.delivery_address.emirate}, UAE</div>
            {order.delivery_address.instructions && (
              <div className="text-muted-foreground">Note: {order.delivery_address.instructions}</div>
            )}
            <div className="text-muted-foreground">Payment: {order.payment.method.replace(/_/g, " ")}</div>
          </CardContent>
        </Card>
        <div className="flex gap-3">
          <Button asChild variant="outline"><Link to="/catalog">Keep shopping</Link></Button>
          <Button asChild variant="ghost"><a href={`https://wa.me/971527129647?text=Hi,%20order%20${order.order_number}`} target="_blank" rel="noreferrer">WhatsApp us</a></Button>
        </div>
      </main>
    </div>
  );
}
